import { useState } from 'react'
import cn from 'classnames'
import { Checkbox } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { ButtonsSection } from 'pages/find-my-classes/components/Quiz/components/ButtonsSection/ButtonsSection'
import { InteractiveStep } from 'pages/find-my-classes/components/Quiz/components/InteractiveStep'
import { useQuizEvents } from 'pages/find-my-classes/components/Quiz/events'
import { CERTS_QUIZ_PLANS_I18N_NAMESPACE } from '../../i18n'

const OPTIONS = ['videos', 'exercises', 'feedback'] as const

type Option = (typeof OPTIONS)[number]

export const LearningPreference = () => {
  const { t } = useTranslation(CERTS_QUIZ_PLANS_I18N_NAMESPACE)
  const { t: tApp } = useTranslation('@mc/next')
  const { trackOptionSelected, trackAnswerSubmitted } = useQuizEvents()
  const [selected, setSelected] = useState<Set<Option>>(new Set())

  const copyFor = (option: Option | 'all') =>
    t(`learningPreference.options.${option}`)

  const allSelected = selected.size === OPTIONS.length
  const selectedOptions = OPTIONS.filter((option) => selected.has(option)).map(
    copyFor,
  )

  const toggleOption = (option: Option) => {
    const checked = !selected.has(option)
    trackOptionSelected({ value: copyFor(option), checked })
    setSelected((current) => {
      const next = new Set(current)
      if (checked) next.add(option)
      else next.delete(option)
      return next
    })
  }

  // "All of the above" is a shortcut, not an answer: its checked state is
  // derived, and it reports one event per option it actually changed so the
  // shortcut label never reaches analytics.
  const toggleAll = () => {
    const checked = !allSelected
    OPTIONS.filter((option) => selected.has(option) !== checked).forEach(
      (option) => trackOptionSelected({ value: copyFor(option), checked }),
    )
    setSelected(checked ? new Set(OPTIONS) : new Set())
  }

  const rows: {
    key: Option | 'all'
    checked: boolean
    onChange: () => void
  }[] = [
    ...OPTIONS.map((option) => ({
      key: option,
      checked: selected.has(option),
      onChange: () => toggleOption(option),
    })),
    { key: 'all' as const, checked: allSelected, onChange: toggleAll },
  ]

  return (
    <InteractiveStep title={t('learningPreference.title')}>
      <div className='mc-my-7'>
        <div className='row no-gutters-vertical justify-content-center'>
          <div className='col-12 col-md-10 col-lg-7'>
            <div className='mc-my-2'>
              {rows.map(({ key, checked, onChange }, index) => (
                <Checkbox
                  key={key}
                  className={cn('mc-p-6 mc-my-1 mc-text--left mc-bg-gray-100', {
                    'mc-bg-gray-300': checked,
                    'mc-corners-top-left--sm mc-corners-top-right--sm':
                      index === 0,
                    'mc-corners-bottom-left--sm mc-corners-bottom-right--sm':
                      index === rows.length - 1,
                  })}
                  checked={checked}
                  label={copyFor(key)}
                  option={key}
                  onChange={onChange}
                  name={`learning-preference-${key}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <ButtonsSection
        disabled={selected.size === 0}
        onNext={() => trackAnswerSubmitted({ selectedOptions })}
        machineEvent={{
          machineEventType: 'learning_preference' as const,
          learningPreferences: selectedOptions,
        }}
        disabledText={tApp(
          'findMyClasses.steps.motivation.a11y.disabledDisclaimer',
        )}
      />
    </InteractiveStep>
  )
}
